import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, Send, Users } from 'lucide-react';

import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@design-system';
import { useToast } from '@client/components/hooks/use-toast';
import {
  selectAdvisorHierarchyState,
  selectSessionAdvisor,
  selectSupervisorAdvisorRelationships,
} from '@client/model/selectors';
import { fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest } from '@client/model/advisorHierarchyModel';
import { clientsApi } from '@client/api/waterlily';
import { isClientUnderNewPartnerConsent } from '@shared';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useDistributionPartnerLegacyShareConsent } from '@/features/distribution-partners';

import { useShareClientWithSupervisors } from './use-share-client';
import { LegacyShareAttestationDialog } from './legacy-share-attestation-dialog';

type Step = 'select' | 'note';

interface ShareClientWithSupervisorDialogProps {
  client: Client;
  partner?: Client | null;
  open: boolean;
  onClose: () => void;
}

function getClientDisplayName(c: Client): string {
  if (c.clientFirstName && c.clientLastName) {
    return `${c.clientFirstName} ${c.clientLastName}`;
  }
  return c.clientEmail || 'this client';
}

const MAX_NOTE_LENGTH = 2000;

function getSupervisorDisplayName(rel: SupervisorAdvisorRelationship): string {
  const name =
    rel.advisorFirstName && rel.advisorLastName
      ? `${rel.advisorFirstName} ${rel.advisorLastName}`
      : null;
  return name ?? rel.advisorEmail;
}

function getSupervisorOrgName(rel: SupervisorAdvisorRelationship): string | null {
  return rel.organizationDisplayName || rel.organizationName || null;
}

function matchesSearch(rel: SupervisorAdvisorRelationship, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = [
    rel.advisorFirstName,
    rel.advisorLastName,
    rel.advisorEmail,
    rel.organizationDisplayName,
    rel.organizationName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function ShareClientWithSupervisorDialog({
  client,
  partner,
  open,
  onClose,
}: ShareClientWithSupervisorDialogProps) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const supervisors = useSelector(selectSupervisorAdvisorRelationships);
  const { relationshipsFetchStatus } = useSelector(selectAdvisorHierarchyState);

  const hasPartner = !!partner && !partner.isSampleClient;

  const isEdjPilot = useIsEdjPilotAdvisor();
  const { hasLegacyShareConsent } = useDistributionPartnerLegacyShareConsent();

  const [step, setStep] = useState<Step>('select');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [includePartner, setIncludePartner] = useState(false);
  const [note, setNote] = useState('');
  const [attestationOpen, setAttestationOpen] = useState(false);

  // Sharing a legacy (pre-cutoff) client requires the advisor to attest they hold the consents to
  // share it — unless they've already done the batch "share all earlier clients" attestation. EDJ
  // advisors have no legacy flow.
  const needsLegacyAttestation =
    !isEdjPilot &&
    !hasLegacyShareConsent &&
    !isClientUnderNewPartnerConsent(client.clientAddedDateTime);

  const { shareWithSupervisors, isPending } = useShareClientWithSupervisors(client.clientId);

  const sharedSupervisorsQuery = useQuery({
    queryKey: ['clientSharedSupervisors', client.clientId],
    queryFn: () => clientsApi.getSharedSupervisors(client.clientId),
    enabled: open && !!client.clientId,
  });

  const advisorId = sessionAdvisor?.advisorId;

  useEffect(() => {
    if (open && advisorId && relationshipsFetchStatus === 'idle') {
      dispatch(fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({ advisorId }));
    }
  }, [open, advisorId, relationshipsFetchStatus, dispatch]);

  useEffect(() => {
    if (open) {
      setStep('select');
      setSearch('');
      setSelectedIds([]);
      setIncludePartner(false);
      setNote('');
      setAttestationOpen(false);
    }
  }, [open]);

  const filteredSupervisors = useMemo(() => {
    if (!supervisors) return [];

    return supervisors.filter(rel => matchesSearch(rel, search));
  }, [supervisors, search]);

  // Supervisors who can already see this client — surfaced with an "Already has access" badge so
  // the advisor knows, but left unchecked so re-sharing (and its email) is always a deliberate pick.
  const alreadySharedIds = useMemo(
    () => new Set(sharedSupervisorsQuery.data?.supervisorAdvisorIds ?? []),
    [sharedSupervisorsQuery.data],
  );

  const isLoadingSupervisors = relationshipsFetchStatus === 'loading';
  const hasNoSupervisors = !isLoadingSupervisors && (supervisors?.length ?? 0) === 0;

  const toggleSupervisor = (advisorIdToToggle: string) => {
    setSelectedIds(prev =>
      prev.includes(advisorIdToToggle)
        ? prev.filter(id => id !== advisorIdToToggle)
        : [...prev, advisorIdToToggle],
    );
  };

  const handleClose = () => {
    if (isPending) return;

    onClose();
  };

  const shouldIncludePartner = hasPartner && includePartner;

  const handleShare = () => {
    shareWithSupervisors(
      {
        supervisorAdvisorIds: selectedIds,
        note: note.trim().length > 0 ? note.trim() : null,
        alsoSharePartner: shouldIncludePartner,
      },
      {
        onSuccess: () => {
          const supervisorWord = selectedIds.length === 1 ? 'supervisor' : 'supervisors';
          const clientCount = shouldIncludePartner ? 2 : 1;
          const clientWord = clientCount === 1 ? 'client' : 'clients';

          toast({
            title: 'Client shared',
            description: `Shared ${clientCount} ${clientWord} with ${selectedIds.length} ${supervisorWord}.`,
            className: '-translate-y-5',
          });
          onClose();
        },
        onError: err => {
          toast({
            title: 'Failed to share client',
            description:
              err instanceof Error ? err.message : 'Something went wrong. Please try again.',
            variant: 'destructive',
            className: '-translate-y-5',
          });
        },
      },
    );
  };

  const clientDisplayName = getClientDisplayName(client);
  const partnerDisplayName = partner ? getClientDisplayName(partner) : null;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={isOpen => !isOpen && handleClose()}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-primary-dark">Share Client</DialogTitle>
            <DialogDescription className="text-foreground">
              {step === 'select'
                ? 'Choose which supervisor(s) to share this client with'
                : 'Add an optional note'}
            </DialogDescription>
          </DialogHeader>

          {step === 'select' && (
            <>
              <div className="py-2">
                <div className="mb-4 flex items-start gap-3">
                  <div className="bg-primary/5 flex size-12 shrink-0 items-center justify-center rounded-full">
                    <Users className="text-primary-dark h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-0.5 text-base font-semibold text-gray-950">
                      Share <span className="text-primary-dark">{clientDisplayName}</span> with
                    </h3>
                    <p className="text-foreground text-sm">
                      Selected supervisors will be able to view this client.
                    </p>
                  </div>
                </div>

                {hasPartner && partnerDisplayName && (
                  <label
                    htmlFor="share-include-partner"
                    className="bg-muted/50 hover:bg-muted mb-3 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                  >
                    <Checkbox
                      id="share-include-partner"
                      checked={includePartner}
                      onCheckedChange={checked => setIncludePartner(checked === true)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-950">
                        Also share their partner ({partnerDisplayName})
                      </p>
                      <p className="text-foreground text-xs">
                        The same supervisors will get access to the partner record with the same
                        note.
                      </p>
                    </div>
                  </label>
                )}

                <div className="relative mb-3">
                  <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, or organization…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <div className="max-h-72 overflow-y-auto rounded-md border">
                  {isLoadingSupervisors ? (
                    <div className="text-foreground flex items-center justify-center gap-2 py-8 text-sm">
                      <Loader2 className="size-4 animate-spin" />
                      Loading supervisors…
                    </div>
                  ) : hasNoSupervisors ? (
                    <div className="text-foreground py-8 text-center text-sm">
                      You're not on any team yet. To share clients, you need to be a member of an
                      organization first.
                    </div>
                  ) : filteredSupervisors.length === 0 ? (
                    <div className="text-foreground py-8 text-center text-sm">
                      No supervisors match "{search}".
                    </div>
                  ) : (
                    <ul className="divide-border divide-y">
                      {filteredSupervisors.map(rel => {
                        const id = `supervisor-${rel.advisorId}`;
                        const orgName = getSupervisorOrgName(rel);
                        const checked = selectedIds.includes(rel.advisorId);
                        return (
                          <li key={rel.advisorId}>
                            <label
                              htmlFor={id}
                              className="hover:bg-muted flex cursor-pointer items-start gap-3 p-3 transition-colors"
                            >
                              <Checkbox
                                id={id}
                                checked={checked}
                                onCheckedChange={() => toggleSupervisor(rel.advisorId)}
                                className="mt-1"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-950">
                                  {getSupervisorDisplayName(rel)}
                                </p>
                                <p className="text-foreground truncate text-xs">
                                  {rel.advisorEmail}
                                </p>
                                {orgName && (
                                  <p className="text-muted-foreground truncate text-xs">
                                    {orgName}
                                  </p>
                                )}
                                {alreadySharedIds.has(rel.advisorId) && (
                                  <Badge variant="success">Already has access</Badge>
                                )}
                              </div>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {selectedIds.length > 0 && (
                  <p className="text-foreground mt-2 text-xs">{selectedIds.length} selected</p>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => setStep('note')}
                  disabled={selectedIds.length === 0}
                >
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}

          {step === 'note' && (
            <>
              <div className="py-2">
                <div className="mb-4">
                  <h3 className="mb-1 text-base font-semibold text-gray-950">
                    Add a note (optional)
                  </h3>
                  <p className="text-foreground text-sm">
                    Share context with the recipient, for example, "Please review the quotes I
                    pulled."
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="share-note"
                    className="text-sm"
                  >
                    Note
                  </Label>
                  <textarea
                    id="share-note"
                    rows={5}
                    maxLength={MAX_NOTE_LENGTH}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Optional note…"
                    className="bg-background placeholder:text-muted-foreground focus:ring-primary flex w-full rounded-md border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
                  />
                  <p className="text-muted-foreground text-xs">
                    {note.length} / {MAX_NOTE_LENGTH}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setStep('select')}
                  disabled={isPending}
                >
                  Back
                </Button>
                <Button
                  onClick={() =>
                    needsLegacyAttestation ? setAttestationOpen(true) : handleShare()
                  }
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-1 size-4 animate-spin" />
                      Sharing…
                    </>
                  ) : (
                    <>
                      <Send className="mr-1 size-4" />
                      Share
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <LegacyShareAttestationDialog
        clientId={client.clientId}
        open={attestationOpen}
        onConfirmed={() => {
          setAttestationOpen(false);
          handleShare();
        }}
        onCancel={() => setAttestationOpen(false)}
      />
    </>
  );
}
